/**
 * Pure render helpers + defaults for the Portless (ThreadLab inbound) manifest
 * email subject + body. Single source of truth shared between the backend send
 * path (portless-export/service.ts) and the frontend live preview
 * (PortlessManifestSettingsSection.tsx) so the two can never drift — BLU-600.
 *
 * The body template is stored/edited as PLAIN TEXT. The send path derives the
 * HTML by escaping the interpolated text and wrapping each line in <p>…</p>;
 * this reproduces the historical hardcoded email's structure on the defaults —
 * the only intentional change is the {type} label, unified to the title-case
 * "Cross Dock" / "Hybrid" in both subject and body (previously "CROSS DOCK" in
 * the subject and "(CrossDock)" in the body). It also removes the HTML-injection
 * surface (no sanitizer dependency needed) since admin-authored text is escaped,
 * never rendered as markup.
 *
 * No I/O, no side effects.
 */
/** The only placeholders an editable template may reference. */
export const PORTLESS_PLACEHOLDER_TOKENS = ['type', 'date', 'summary'];
/**
 * Default subject template. Matches the historical subject except `{type}` is
 * the title-case label ("Cross Dock" / "Hybrid" vs the old "CROSS DOCK" /
 * "HYBRID").
 */
export const DEFAULT_PORTLESS_SUBJECT_TEMPLATE = '{type} - ThreadLab inbound packing list - {date}';
/**
 * Default body template (plain text). Split on `\n`, each line becomes a <p> in
 * the HTML part — reproducing the original three-paragraph body.
 */
export const DEFAULT_PORTLESS_BODY_TEMPLATE = 'ThreadLab inbound packing list ({type}) for {date}.\n{summary}\nGenerated automatically by ThreadHeads IMS.';
/** Escape the 5 HTML metacharacters. Mirrors escHtml in cx-notification.email.ts. */
export function escHtmlForManifest(s) {
    return (s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Interpolate `{type}`/`{date}`/`{summary}` into a template. Uses a single-pass
 * function replacer over an allowlist: unknown tokens (e.g. `{orderCount}`) never
 * match and are left literal (no `undefined` leak), and a function replacer avoids
 * `String.prototype.replace`'s `$&`/`$1` special-sequence footgun if a value ever
 * contains `$`.
 */
export function renderManifestText(template, vars) {
    return template.replace(/\{(type|date|summary)\}/g, (_match, token) => {
        switch (token) {
            case 'type':
                return vars.type;
            case 'date':
                return vars.date;
            case 'summary':
                return vars.summary;
            default:
                return _match;
        }
    });
}
/**
 * Render the HTML body: interpolate the plain-text template, HTML-escape the
 * result, then wrap each `\n`-separated line in `<p>…</p>`. Escaping AFTER
 * interpolation means both the admin-authored text and the substituted values
 * are inert as markup.
 */
export function renderManifestHtml(template, vars) {
    const escaped = escHtmlForManifest(renderManifestText(template, vars));
    return escaped
        .split('\n')
        .map((line) => `<p>${line}</p>`)
        .join('');
}
